import { Injector } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import jwt_decode from 'jwt-decode';

import { environment } from '@env/environment';
import { BitfTryCatch } from '../../../decorators';
import { EBitfAuthState } from '../auth.enum';
import { BitfAuthService } from '../bitf-auth.service';
import { BitfStrapiProviderService } from './bitf-strapi-provider.service';
import {
  IBitStrapiProviderLogin,
  IBitStrapiProviderTokenMetadata,
} from './bitf-strapi-provider-auth.interface';
import { EBitfStrapiProvider } from './bitf-strapi-provider-auth.enum';
import { IBitfApiResponse } from '../../api/bitf-api.interface';
import { User } from '@common/core/models';

export abstract class BitfStrapiProviderAuthService extends BitfAuthService<
  IBitStrapiProviderLogin,
  IBitStrapiProviderTokenMetadata
> {
  private activatedRoute: ActivatedRoute;
  private bitfStrapiProviderService: BitfStrapiProviderService;

  constructor(protected injector: Injector) {
    super(injector);
    this.activatedRoute = injector.get(ActivatedRoute);
    this.bitfStrapiProviderService = injector.get(BitfStrapiProviderService);
  }

  @BitfTryCatch()
  handleAuthentication(provider: EBitfStrapiProvider) {
    const accessToken = this.activatedRoute.snapshot.queryParamMap.get('access_token');
    if (accessToken) {
      this.authState$.next(EBitfAuthState.LOGIN_IN_PROGRESS);
      this.bitfStrapiProviderService.getToken(accessToken, provider).subscribe(response => {
        this.signIn(response.content);
        this.authState$.next(EBitfAuthState.LOGGED);
      });
    } else if (super.isTokenValid()) {
      this.usersService.getMe().subscribe(
        (response: IBitfApiResponse<User>) => {
          this.setUser(response.content);
          this.authState$.next(EBitfAuthState.LOGGED);
        },
        () => {
          this.onLoginError();
        }
      );
    } else {
      this.authState$.next(EBitfAuthState.TO_BE_LOGGED);
      window.location.href = environment['baseStrapiProviderAuthUrl'] + provider;
    }
  }

  @BitfTryCatch()
  decodeToken(loginResponse: IBitStrapiProviderLogin): IBitStrapiProviderTokenMetadata {
    if (!loginResponse) {
      return undefined;
    }

    const decodedToken: any = jwt_decode(loginResponse.jwt);
    Object.assign(decodedToken, {
      token: loginResponse.jwt,
      expiresAt: decodedToken.exp * 1000,
      iat: decodedToken.iat * 1000,
      exp: decodedToken.exp * 1000,
    });
    return decodedToken as IBitStrapiProviderTokenMetadata;
  }
}
